import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';


@Injectable({
	providedIn: 'root'
})
export class ValidationService {
	passwordRequirements: any[] = [
		{
			description: "Must be at least 8 characters",
			valid: false
		},
		{
			description: "Must include uppercase and lowercase characters", 
			valid: false
		},
		{
			description: "Must include a number",
			valid: false
		},
		{
			description: "Must include a special character",
			valid: false
		},
		{
			description: "Password should match",
			valid: false
		}
	];

	constructor() { }

	uppercaseLowercaseValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return /(?=.*[a-z])(?=.*[A-Z])/.test(value) ? null : { uppercaseLowercase: true };
		};
	}

	numberValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return /\d/.test(value) ? null : { number: true };
		};
	}

	specialCharValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return /[!@#$%^&*(),.?":{}|<>]/.test(value) ? null : { specialChar: true };
		};
	}

	passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
		const newPassword = control.get('newPassword');
		const confirmPassword = control.get('confirmPassword');
		if (!newPassword || !confirmPassword) return null;
		return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
	}

	updatePasswordRequirements(form: AbstractControl): void {
		const newPasswordControl = form.get('newPassword');
		if (newPasswordControl) {
		const value = newPasswordControl.value;
	
		if (!value) {
			this.passwordRequirements.forEach(req => (req.valid = false));
		} else {
			this.passwordRequirements[0].valid = value.length >= 8;
			this.passwordRequirements[1].valid = /(?=.*[a-z])(?=.*[A-Z])/.test(value);
			this.passwordRequirements[2].valid = /\d/.test(value);
			this.passwordRequirements[3].valid = /[!@#$%^&*(),.?":{}|<>]/.test(value);
		}
		}

		const confirmPasswordControl = form.get('confirmPassword');
		const newPasswordValue = form.get('newPassword')?.value;
		if (confirmPasswordControl?.value && newPasswordValue) {
			const passwordsMatch = newPasswordValue === confirmPasswordControl.value;
			this.passwordRequirements[4].valid = passwordsMatch;
		} else {
			this.passwordRequirements[4].valid = false;
		}
	}  

	dateValidator(control: AbstractControl): {[key: string]: any} | null {
		if (!control.value) {
			return null; // Let required validator handle empty values
		}

		const date = new Date(control.value);

		// Check if it's a valid date
		if (Number.isNaN(date.getTime())) {
			return { 'invalidDate': true };
		}

		return null;
	}

	// minDateValidator(minDate: Date): (control: AbstractControl) => ValidationErrors | null {
	// 	return (control: AbstractControl): ValidationErrors | null => {
	// 	if (!control.value) {
	// 		return null; // Let required validator handle empty values
	// 	}

	// 	const date = new Date(control.value);
	// 	date.setHours(0,0,0,0)

	// 	// Check if it's a valid date
	// 	if (isNaN(date.getTime())) {
	// 		return null;
	// 	}

	// 	// Check if the date is before the minimum date
	// 	if (date <= minDate) {
	// 		return { 'minDate': true };
	// 	}

	// 	return null;
	// 	};
	// }

	/**
	 * Validates that a date is after or equal to the minimum date
	 */
	minDateValidator(minDate: Date): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value) {
				return null;
			}
			
			const controlDate = new Date(control.value);			
			return controlDate < minDate ? { 'minDate': { 'required': minDate, 'actual': controlDate } } : null;
		};
	}

	/**
	 * Validates that a date is before or equal to the maximum date
	 */
	maxDateValidator(maxDate: Date): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value) {
				return null;
			}
			
			const controlDate = new Date(control.value);
			return controlDate > maxDate ? { 'maxDate': { 'required': maxDate, 'actual': controlDate } } : null;
		};
	}

	/**
	 * Creates a validator that ensures the end date is after the start date
	 */
	endDateAfterStartDateValidator(startDateControlName:string): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value || !control.parent) {
				return null;
			}
			
			const endDate = new Date(control.value);
			const startDateControl = control.parent.get(startDateControlName);
			
			if (!startDateControl || !startDateControl.value) {
				return null;
			}
			
			const startDate = new Date(startDateControl.value);
			
			return endDate < startDate ? { 'endDateBeforeStartDate': { 'startDate': startDate, 'endDate': endDate } } : null;
		};
	}

	/**
	 * Creates a validator that makes a control required when another control has a value
	 * @param parentControlName The name of the control that triggers the requirement
	 * @returns A validator function that returns an error if the child control is empty when parent has value
	 */
	requireOtherFieldOnInput(parentControlName: string): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.parent) {
				return null;
			}
			
			const parentControl = control.parent.get(parentControlName);
			
			if (!parentControl) {
				return null;
			}
			
			// If parent control has a value, make this control required
			if (parentControl.value) {
				return !control.value ? { 'childRequired': true } : null;
			}
			
			return null;
		};
	}

	/**
	 * Validates that a number has at most the specified number of decimal places
	 * @param decimalPlaces The maximum number of decimal places allowed (default: 2)
	 * @returns A validator function that returns an error if the value has more than the specified decimal places
	 */
	decimalPlacesValidator(decimalPlaces: number = 2): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			
			// Skip validation if value is empty
			if (value === null || value === undefined || value === '') {
				return null;
			}
			
			// Convert to string and check if it's a valid number
			const stringValue = value.toString();
			if (!/^-?\d*(\.\d*)?$/.test(stringValue)) {
				return { 'notANumber': true };
			}
			
			// Check decimal places
			const parts = stringValue.split('.');
			if (parts.length > 1 && parts[1].length > decimalPlaces) {
				return { 'maxDecimalPlaces': { actual: parts[1].length, max: decimalPlaces } };
			}
			
			return null;
		};
	}

	/**
	 * Validates that a value is a whole number (integer)
	 * @returns A validator function that returns an error if the value is not a whole number
	 */
	wholeNumberValidator(): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			
			// Skip validation if value is empty
			if (value === null || value === undefined || value === '') {
				return null;
			}
			
			// Check if it's a valid number and a whole number (integer)
			if (!Number.isInteger(Number(value))) {
				return { 'notWholeNumber': true };
			}
			
			return null;
		};
	}

	validateArrayNotEmpty(c: FormControl) {
		if (c.value && c.value.length === 0) {
		return {
			required: true
		};
		}
		return null;
	}

	/**
	 * Validates that input contains only alphanumeric characters (no spaces)
	 * @returns A validator function that returns an error if the value contains non-alphanumeric characters or spaces
	 */
	alphanumericValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return /^[a-zA-Z0-9]+$/.test(value) ? null : { alphanumeric: true };
		};
	}

	alphanumericIncludeHyphenValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return /^[a-zA-Z0-9\-]+$/.test(value) ? null : { alphanumericIncludeHyphen: true };
		};
	}

	alphanumericIncludeSpecialCharacters(additionalChars:string) {
		const regexPattern = new RegExp(`^[a-zA-Z0-9${additionalChars}]+$`);
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return regexPattern.test(value) ? null : { alphanumericIncludeSpecialCharacters: true };
		};
	}

	isObjectValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
	
		if(value === null) {
			return null
		}
	
		if(value === "") {
			return null
		}
	
		if (typeof value !== 'object' || Array.isArray(value)) {
			return { 
				notObject: {
					value: value,
					message: 'Value must be an object'
				}
			};
		}
		
		return null;
	};

	/**
	 * Validates that a numeric value is less than the specified maximum
	 * @param maxValue The maximum allowed value (exclusive)
	 * @returns A validator function that returns an error if the value is greater than or equal to maxValue
	 */
	maxValueValidator(maxValue: number): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			
			// Skip validation if value is empty
			if (value === null || value === undefined || value === '') {
				return null;
			}
			
			// Convert to number and validate
			const numValue = Number(value);
			if (isNaN(numValue)) {
				return { 'notANumber': true };
			}
			
			// Check if value is less than maxValue
			return numValue < maxValue ? null : { 'maxValue': { actual: numValue, max: maxValue } };
		};
	}

	/**
	 * Inverse of required validator - validates that a field is empty
	 * @returns A validator function that returns an error if the value is not empty
	 */
	notRequiredValidator(): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			// Return error if value exists (not null, undefined, or empty string)
			if (value !== null && value !== undefined && value !== '') {
				return { 'notRequired': true };
			}
			return null;
		};
	}

	/**
	 * Validates that the input is a properly formatted email using a custom pattern
	 * @returns A validator function that returns an error if the value is not a valid email
	*/
	emailValidator(): (control: AbstractControl) => ValidationErrors | null {
		const EMAIL_PATTERN = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]{2,}$/;
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			return EMAIL_PATTERN.test(value) ? null : { invalidEmail: true };
		};
	}

	// exceeds allowed quantity
	quantityValidator(availableQuantity: number) {
		return (control: any) => {
			const value = control.value;
			if (value && value > availableQuantity) {
				return { exceedsAvailable: true };
			}
			return null;
		};
	}

	pathTraversalValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value) return null;

			const value = control.value.toString().trim().toLowerCase();

			const restrictions = [
				{
					patterns: ["..", "--", ":/", ":\\\\", "./"],
					check: (v: string, pattern: string) => v.includes(pattern),
					message: (pattern: string) => `Must not contain "${pattern}"`,
				},
				{
					patterns: ["\\\\", "/", "."],
					check: (v: string, pattern: string) => v.startsWith(pattern),
					message: (pattern: string) => `Must not start with "${pattern}"`
				},
				{
					patterns: [["truncate", ";"], ["drop", ";"], ["insert", "into"], ["update", "set"]],
					check: (v: string, pattern: string[]) => v.includes(pattern[0]) && v.includes(pattern[1]),
					message: (pattern: string[]) => `Must not contain "${pattern[0]}" and "${pattern[1]}"`,
				}
			];

			for (const restriction of restrictions) {
				const match = restriction.patterns.find(pattern => restriction.check(value, pattern as any));
				if (match) {
					return {
						'path-traversal': {
							"error_message": "Invalid Input" // restriction.message(match as any)
						}
					};
				}
			}

			return null;
		}
	}

	/**
	 * Validates that a numeric value is not equal to the specified value
	 * @param excludedValue The value that should not be allowed
	 * @returns A validator function that returns an error if the value equals excludedValue
	 */
	notEqualValidator(excludedValue: number): (control: AbstractControl) => ValidationErrors | null {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			
			// Skip validation if value is empty
			if (value === null || value === undefined || value === '') {
				return null;
			}
			
			// Convert to number and validate
			const numValue = Number(value);
			if (isNaN(numValue)) {
				return { 'notANumber': true };
			}
			
			// Check if value equals excludedValue
			return numValue === excludedValue ? { 'notEqual': { actual: numValue, excluded: excludedValue } } : null;
		};
	}
}
